import React, { useState, useEffect, useRef, useCallback } from "react";
import * as echarts from "echarts";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import Loader from "react-spinner-loader";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faCity } from "@fortawesome/free-solid-svg-icons";
import { Expand } from "../../../../Components/IconComponents";
import LMSButton from "../../Common/LMSButton/LMSButton";
import { getData, postDataWithBody } from "../../../../Services/Services";
import { avalanchecardsdata, avalanchechartdata, avalanchepopupdata } from "../../../../Services/EndPoints";
import "react-datepicker/dist/react-datepicker.css";
import "./AvalancheDashboard.css";

// ── Blue-only palette (matching cold wave tokens) ────────────────────────
const C = {
  bg: "#F0F6FB",
  surface: "#FFFFFF",
  navBg: "#1C4E72",
  primary: "#2A6591",
  text1: "#0F2D42",
  text2: "#3A5F78",
  text3: "#7A9BB0",
  border: "rgba(42,101,145,.10)",
};

// ── Pastel icon palette (same as ColdWaveDashboardcard) ──────────────────
const PALETTE = [
  { bg: "#FFF0F0", color: "#D95555" },
  { bg: "#FFF4EA", color: "#D4772A" },
  { bg: "#EEFAF3", color: "#27A05A" },
  { bg: "#EBF4FF", color: "#3579D4" },
];

// ── Card Config ───────────────────────────────────────────────────────────
const dashboardCardConfigForAvalanche = {
  data: [
    { key: "no_of_deaths_today", label: "Deaths" },
    { key: "no_of_persons_hospitalized_today", label: "Hospitalized Today" },
    { key: "no_of_persons_hospitalized_pay_done_today", label: "Hospitalized Payments" },
    { key: "no_of_dec_ex_gratia_pay_done_today", label: "Ex-Gratia Payments" },
  ],
};

const formatLabel = (key) => ({
  no_of_deaths_today: "Deaths",
  no_of_persons_hospitalized_today: "Hospitalized",
  no_of_persons_hospitalized_pay_done_today: "Medical Payments",
  no_of_dec_ex_gratia_pay_done_today: "Ex-Gratia Payments",
}[key] || key);

// ── SVG Icons (matching cold wave icon style) ────────────────────────────
function AvalancheIcon({ keyName = "", label = "", color = "#2A6591" }) {
  const k = (keyName + " " + label).toLowerCase();
  const sw = "1.55";
  const r = "round";

  if (k.includes("death") || k.includes("loss") || k.includes("fatal"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <circle cx="10" cy="6.5" r="2.8" stroke={color} strokeWidth={sw}/>
        <path d="M4.5 18c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" stroke={color} strokeWidth={sw} strokeLinecap={r}/>
        <path d="M7.8 5l4.4 4.4M12.2 5l-4.4 4.4" stroke={color} strokeWidth="1.4" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("hospital") && !k.includes("pay"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <rect x="3" y="3" width="14" height="14" rx="4" stroke={color} strokeWidth={sw}/>
        <path d="M10 7v6M7 10h6" stroke={color} strokeWidth="1.8" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("hospital") && k.includes("pay"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <rect x="3" y="3" width="14" height="14" rx="4" stroke={color} strokeWidth={sw}/>
        <path d="M10 7v6M7 10h6" stroke={color} strokeWidth="1.8" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("gratia") || k.includes("compens"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <path d="M10 2.5L3.5 5v5.5c0 4 2.9 6.5 6.5 7 3.6-.5 6.5-3 6.5-7V5L10 2.5z"
          stroke={color} strokeWidth={sw} strokeLinejoin={r}/>
        <path d="M7.5 10l2 2 3-3.5" stroke={color} strokeWidth="1.5" strokeLinecap={r} strokeLinejoin={r}/>
      </svg>
    );

  return (
    <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
      <path d="M2 16l5.5-10 3 5 2-3L17 16H2z" stroke={color} strokeWidth={sw} strokeLinejoin={r}/>
      <circle cx="14" cy="5" r="2" stroke={color} strokeWidth="1.3"/>
    </svg>
  );
}

// ── Popup ──────────────────────────────────────────────────────────────────
const DistrictPopup = ({ show, onHide, districtData, incidentType, blocks }) => {
  if (!show) return null;

  return (
    <div className="avd-popup-overlay" onClick={onHide}>
      <div className="avd-popup" onClick={e => e.stopPropagation()}>
        <div className="avd-popup-header">
          <div className="avd-popup-header-left">
            <div className="avd-popup-logo">
              <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                <path d="M2 16l5.5-10 3 5 2-3L17 16H2z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
                <circle cx="14" cy="5" r="2" stroke="white" strokeWidth="1.2"/>
              </svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="avd-popup-overline">Avalanche Impact Zone</div>
              <div className="avd-popup-title-text">{districtData} • {formatLabel(incidentType)}</div>
            </div>
          </div>
          <button className="avd-popup-close" onClick={onHide}>✕</button>
        </div>

        <div className="avd-popup-body">
          {blocks?.length > 0 ? (
            blocks.map((block, i) => (
              <div key={i} className="avd-popup-row">
                <div className="avd-popup-row-left">
                  <div className="avd-popup-row-num">{i + 1}</div>
                  <FontAwesomeIcon icon={faCity} style={{ color: C.primary, fontSize: 12 }} />
                  <span>{block.block_name}</span>
                </div>
                <div className="avd-popup-row-badge">
                  {block.count.toLocaleString("en-IN")}
                </div>
              </div>
            ))
          ) : (
            <div className="avd-popup-empty">
              <FontAwesomeIcon icon={faInfoCircle} className="avd-popup-empty-icon" />
              <div className="avd-popup-empty-text">No Block Data Available</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ─────────────────────────────────────────────────────────
export default function AvalancheDashboard() {
  const handle = useFullScreenHandle();
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cardData, setCardData] = useState({});
  const [selectedIncident, setSelectedIncident] = useState("");
  const [popupState, setPopupState] = useState({ show: false, district: null, blocks: [], incidentType: null });
  const [isTrendExpanded, setIsTrendExpanded] = useState(false);
  const [isTrendMinimized, setIsTrendMinimized] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);

  // ✅ FIX: Match original — from Jan 1 current year to today
  const [internalFromDate, setInternalFromDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [internalToDate, setInternalToDate] = useState(new Date());
  const [appliedFromDate, setAppliedFromDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [appliedToDate, setAppliedToDate] = useState(new Date());

  // ✅ FIX: Derive date strings from applied dates (safe against null)
  const startDateStr = appliedFromDate ? moment(appliedFromDate).format("YYYY-MM-DD") : "";
  const endDateStr = appliedToDate ? moment(appliedToDate).format("YYYY-MM-DD") : "";

  // ✅ FIX: fetchCardData reads the passed params, not stale state
  const fetchCardData = useCallback(async (fromDate, toDate) => {
    const s = moment(fromDate).format("YYYY-MM-DD");
    const e = moment(toDate).format("YYYY-MM-DD");
    setLoader(true);
    setError(null);
    try {
      const data = await getData(avalanchecardsdata.replace("FROMDATE", s).replace("TODATE", e));
      setCardData(data?.[0] || {});
    } catch {
      setError("Failed to load data.");
    } finally {
      setLoader(false);
    }
  }, []);
  const ReadOnlyDateInput = React.forwardRef((props, ref) => (
  <input {...props} ref={ref} onKeyDown={e => e.preventDefault()} readOnly />
));

  // ✅ FIX: Fetch on initial mount with correct dates
  useEffect(() => {
    fetchCardData(appliedFromDate, appliedToDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ FIX: Apply button — set dates, then fetch with the NEW values directly
  const handleApply = () => {
    if (!internalFromDate || !internalToDate) {
      setError("Please select both From and To dates");
      return;
    }
    if (internalFromDate > internalToDate) {
      setError("From date cannot be greater than To date");
      return;
    }
    setError(null);
    setAppliedFromDate(internalFromDate);
    setAppliedToDate(internalToDate);
    // ✅ Pass the new values directly to avoid stale closure
    fetchCardData(internalFromDate, internalToDate);
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    const today = new Date();
    const newFrom = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const nextTo = today <= yearEnd ? today : yearEnd;
    setInternalFromDate(newFrom);
    setInternalToDate(nextTo);
    setAppliedFromDate(newFrom);
    setAppliedToDate(nextTo);
    fetchCardData(newFrom, nextTo);
  };

  // ✅ FIX: Validate dates safely (handle null from isClearable)
  useEffect(() => {
    if (internalFromDate && internalToDate && internalFromDate > internalToDate) {
      setError("Select proper From Date and To Date");
    } else if (!internalFromDate || !internalToDate) {
      setError(null); // Don't show error when cleared, just disable Apply
    } else {
      setError(null);
    }
  }, [internalFromDate, internalToDate]);

  const handleBarClick = async (district) => {
    try {
      const regionIds = district.region_id.split(",").map(Number);
      const blockData = await postDataWithBody(avalanchepopupdata, {
        region_id_list: regionIds,
        from_date: appliedFromDate,
        to_date: appliedToDate,
        bar_type: selectedIncident,
      }, {});
      setPopupState({ show: true, district: district.dist_name, blocks: blockData || [], incidentType: selectedIncident });
    } catch {
      setPopupState({ show: true, district: district.dist_name, blocks: [], incidentType: selectedIncident });
    }
  };

  const toggleFullscreen = () => {
    isFullscreen ? handle.exit() : handle.enter();
    setIsFullscreen(!isFullscreen);
  };

  // ✅ FIX: Disable Apply when dates are invalid or missing
  const isApplyDisabled = !!error || !internalFromDate || !internalToDate;

  return (
    <FullScreen handle={handle}>
      <div className={`avd-root${isFullscreen ? " avd-fs" : ""}`}>
        <Loader show={loader} type="body" stack="vertical" message="Loading Data" />

        {/* ── NAV ── */}
        <nav className="avd-nav">
          <div className="avd-nav-brand">
            <div className="avd-nav-logo">
              <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                <path d="M2 16l5.5-10 3 5 2-3L17 16H2z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" opacity=".9"/>
                <circle cx="14" cy="5" r="2" stroke="white" strokeWidth="1.2" opacity=".9"/>
              </svg>
            </div>
            <div className="avd-nav-title">Avalanche Dashboard</div>
          </div>

          <div className="avd-nav-right">
            <div className="avd-nav-group">
              <span className="avd-nav-label">Year</span>
              <select className="avd-nav-select" value={selectedYear} onChange={handleYearChange}>
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>

            {/* Date range pill with ReactDatePicker (same as cold wave) */}
            <div className="avd-daterange">
              <div className="avd-daterange-field">
                <span className="avd-dr-label">FROM</span>
                <ReactDatePicker
                  selected={internalFromDate}
                  onChange={(date) => setInternalFromDate(date)}
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  placeholderText="Select date"
                  todayButton="Today"
                  className="avd-dp-input"
                  clearButtonClassName="avd-dp-clear"
                   customInput={<ReadOnlyDateInput />}
                />
              </div>
              <div className="avd-dr-divider" />
              <div className="avd-daterange-field">
                <span className="avd-dr-label">TO</span>
                <ReactDatePicker
                  selected={internalToDate}
                  onChange={(date) => setInternalToDate(date)}
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  placeholderText="Select date"
                  todayButton="Today"
                  className="avd-dp-input"
                  clearButtonClassName="avd-dp-clear"
                   customInput={<ReadOnlyDateInput />}
                />
              </div>
            </div>

            <button className="avd-btn-apply" onClick={handleApply} disabled={isApplyDisabled}>
              Apply
            </button>

            <button className="avd-btn-icon" onClick={toggleFullscreen}>
              {isFullscreen ? (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                  <path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                  <path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              )}
            </button>
          </div>
        </nav>

        {error && (
          <div className="avd-error-strip">
            <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        <div className="avd-content">

          {/* ── HORIZONTAL CARDS ROW (top) ── */}
          <div className="avd-cards-row">
            <div className="avd-cards-inner">
              {dashboardCardConfigForAvalanche.data.map((cfg, idx) => {
                const p = PALETTE[idx % PALETTE.length];
                const active = selectedIncident === cfg.key;
                return (
                  <div
                    key={cfg.key}
                    className={`avd-card-item${active ? " avd-card-item--active" : ""}`}
                    onClick={() => setSelectedIncident(cfg.key)}
                  >
                    <span className="avd-card-icon" style={{ background: p.bg }}>
                      <AvalancheIcon keyName={cfg.key} label={cfg.label} color={p.color} />
                    </span>
                    <span className="avd-card-text">
                      <span className="avd-card-label">{cfg.label}</span>
                      <span className="avd-card-value">
                        {cardData[cfg.key] != null ? cardData[cfg.key].toLocaleString("en-IN") : "0"}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── CHART AREA (below cards, full width) ── */}
          <div className={`avd-chart-wrap${isTrendExpanded ? " avd-chart-wrap--expanded" : ""}`}>
            <div className="avd-chart-top">
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div>
                  <h3 className="avd-chart-heading">
                    {selectedIncident ? formatLabel(selectedIncident) : "Incident Trend Overview"}
                  </h3>
                  {!selectedIncident && (
                    <p className="avd-chart-range">Select any incident from the cards above to view its trend</p>
                  )}
                  {selectedIncident && (
                    <p className="avd-chart-range">
                      {moment(startDateStr).format("DD/MM/YYYY")} — {moment(endDateStr).format("DD/MM/YYYY")}
                    </p>
                  )}
                </div>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {selectedIncident && (
                  <button className="avd-badge-btn" onClick={() => setSelectedIncident("")}>
                    {formatLabel(selectedIncident)} ✕
                  </button>
                )}
                
                {!isTrendExpanded && (
                  <button 
                    className="avd-btn-icon" 
                    style={{ background: "rgba(42,101,145,.08)", border: "1px solid rgba(42,101,145,.12)", color: "var(--av-primary)" }}
                    onClick={() => setIsTrendMinimized(!isTrendMinimized)}
                    title={isTrendMinimized ? "Expand Card" : "Collapse Card"}
                  >
                    {isTrendMinimized ? (
                      <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8M8 4v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    ) : (
                      <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    )}
                  </button>
                )}

                <button 
                  className="avd-btn-icon" 
                  style={{ background: "rgba(42,101,145,.08)", border: "1px solid rgba(42,101,145,.12)", color: "var(--av-primary)" }}
                  onClick={() => setIsTrendExpanded(!isTrendExpanded)}
                  title={isTrendExpanded ? "Collapse Fullscreen" : "Expand Fullscreen"}
                >
                  {isTrendExpanded ? (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M13 7H9V3M9 7L15 1M7 13V9H3M7 9L1 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M10 1H15V6M15 1L9 7M6 15H1V10M1 15L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>
              </div>
            </div>

            {(isTrendExpanded || !isTrendMinimized) && (
              <>
                {/* Empty state */}
                {!selectedIncident && (
                  <div className="avd-chart-empty">
                    <div className="avd-chart-empty-icon">
                      <svg viewBox="0 0 48 48" fill="none" width="40" height="40">
                        <rect x="4" y="30" width="8" height="14" rx="2" fill="#C5DFF0"/>
                        <rect x="16" y="20" width="8" height="24" rx="2" fill="#A0C8E0"/>
                        <rect x="28" y="12" width="8" height="32" rx="2" fill="#7AB4D0"/>
                        <rect x="40" y="22" width="8" height="22" rx="2" fill="#5AA0C0"/>
                        <path d="M4 8h40" stroke="#C5DFF0" strokeWidth="1.5" strokeDasharray="3 3"/>
                      </svg>
                    </div>
                    <p className="avd-chart-empty-text">Click any incident card above to visualize its trend</p>
                  </div>
                )}

                <div className="avd-chart-area">
                  <AvalancheBarChart
                    selectedIncident={selectedIncident}
                    startDateStr={startDateStr}
                    endDateStr={endDateStr}
                    onBarClick={handleBarClick}
                    isTrendExpanded={isTrendExpanded}
                  />
                </div>
              </>
            )}
          </div>

          {/* ── Bottom charts ── */}
          <div className="avd-bottom-charts">
            <DonutChart cardData={cardData} />
            <ColumnChart cardData={cardData} />
            <GaugeChart cardData={cardData} />
          </div>

          {/* ── Popup ── */}
          <DistrictPopup
            show={popupState.show}
            onHide={() => setPopupState({ show: false, district: null, blocks: [], incidentType: null })}
            districtData={popupState.district}
            incidentType={popupState.incidentType}
            blocks={popupState.blocks}
          />
        </div>
      </div>
    </FullScreen>
  );
}

// ── Bar Chart ──────────────────────────────────────────────────────────────
function AvalancheBarChart({ selectedIncident, startDateStr, endDateStr, onBarClick, isTrendExpanded }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedIncident || !startDateStr || !endDateStr) return;
    setLoading(true);
    getData(
      avalanchechartdata
        .replace("FROMDATE", startDateStr)
        .replace("TODATE", endDateStr)
        .replace("BARTYPE", selectedIncident)
    )
      .then(r => setChartData(r || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedIncident, startDateStr, endDateStr]);

  // Dynamic height: 45px per bar, minimum 150px, max 500px
  const validBars = chartData.filter(i => i.count > 0).length;
  const dynamicHeight = isTrendExpanded ? "calc(100vh - 160px)" : Math.max(150, Math.min(500, validBars * 45 + 80));

  useEffect(() => {
    if (!chartRef.current || !chartData.length) return;
    const sorted = [...chartData.filter(i => i.count > 0)].sort((a, b) => a.count - b.count);
    if (!sorted.length) return;

    const option = {
      tooltip: {
        trigger: "axis", axisPointer: { type: "shadow" },
        formatter: p => `<div style="font-weight:700;color:${C.text1};border-bottom:1px solid ${C.border};margin-bottom:6px;padding-bottom:4px">${p[0].name}</div><span style="color:${C.text3}">Count: </span><span style="font-weight:700;color:${C.primary}">${p[0].value.toLocaleString("en-IN")}</span>`,
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);font-family:'Inter',sans-serif;",
      },
      grid: { left: "15%", right: "10%", bottom: 36, top: 20, containLabel: false },
      xAxis: {
        type: "value",
        axisLabel: { color: C.text3, fontSize: 11 },
        splitLine: { lineStyle: { color: C.border, type: "dashed" } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category",
        data: sorted.map(i => i.dist_name),
        axisLabel: { color: C.text1, fontSize: 12, fontWeight: "600", padding: [0, 10, 0, 0] },
        axisLine: { lineStyle: { color: C.border, width: 1 } },
      },
      series: [{
        type: "bar",
        data: sorted.map(item => ({
          value: item.count,
          itemStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [{ offset: 0, color: "#A0C8E0" }, { offset: 1, color: C.primary }],
            },
            borderRadius: [0, 10, 10, 0],
          },
        })),
        barWidth: 15,
        barMaxWidth: 20,
        barCategoryGap: "20%",
        label: { show: true, position: "right", formatter: p => p.value.toLocaleString("en-IN"), fontSize: 11, fontWeight: "700", color: C.primary },
        showBackground: true, backgroundStyle: { color: "rgba(42,101,145,.04)", borderRadius: [0, 10, 10, 0] },
      }],
      dataZoom: sorted.length > 6 ? [{
        type: "slider", yAxisIndex: 0, start: 0, end: 60, orient: "vertical",
        handleStyle: { color: C.primary }, borderColor: C.border, backgroundColor: C.surface,
      }] : [],
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    chartInstance.current.on("click", p => {
      if (p.componentType === "series") {
        const d = chartData.find(x => x.dist_name === p.name);
        if (d && onBarClick) onBarClick(d);
      }
    });
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(chartRef.current);
    return () => { ro.disconnect(); window.removeEventListener("resize", onResize); try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; };
  }, [chartData, selectedIncident, isTrendExpanded]);

  if (!selectedIncident) return null;
  if (loading) return (
    <div style={{ textAlign: "center", padding: 40 }}>
      <Loader show={true} type="body" stack="vertical" message="Analyzing data..." />
    </div>
  );
  if (!chartData.length || !chartData.some(i => i.count > 0)) return (
    <div style={{ textAlign: "center", padding: 40, color: C.text3, fontSize: 13 }}>
      No data available for the selected period
    </div>
  );

  return <div ref={chartRef} style={{ width: "100%", height: dynamicHeight }} />;
}

// ── Donut Chart ───────────────────────────────────────────────────────────
function DonutChart({ cardData }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const keys = dashboardCardConfigForAvalanche.data;
    const total = keys.reduce((s, c) => s + (cardData[c.key] || 0), 0);
    const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0"];
    const seriesData = keys.map((cfg, idx) => ({
      name: formatLabel(cfg.key),
      value: cardData[cfg.key] || 0,
      itemStyle: { color: blueShades[idx], borderRadius: 6, borderColor: C.surface, borderWidth: 3 },
    })).filter(d => d.value > 0);

    const option = {
      title: { text: "IMPACT DISTRIBUTION", left: "center", top: 16, textStyle: { color: C.text1, fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: {
        trigger: "item",
        formatter: p => `<div style="padding:4px 2px"><div style="font-weight:700;color:${C.text1};margin-bottom:4px">${p.name}</div><div style="color:${C.primary};font-weight:800;font-size:18px">${p.value.toLocaleString("en-IN")}</div><div style="color:${C.text3};font-size:11px">${p.percent.toFixed(1)}% of total</div></div>`,
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);",
      },
      legend: { orient: "horizontal", bottom: 12, itemWidth: 10, itemHeight: 10, itemGap: 14, textStyle: { color: C.text3, fontSize: 10, fontWeight: "600" } },
      graphic: [
        { type: "text", left: "center", top: "42%", style: { text: total.toLocaleString("en-IN"), textAlign: "center", fill: C.text1, fontSize: 22, fontWeight: "800" } },
        { type: "text", left: "center", top: "51%", style: { text: "TOTAL", textAlign: "center", fill: C.text3, fontSize: 10, fontWeight: "600", letterSpacing: 2 } },
      ],
      series: [{
        type: "pie", radius: ["42%", "68%"], center: ["50%", "48%"], padAngle: 2,
        label: { show: false },
        emphasis: { scale: true, scaleSize: 6, itemStyle: { shadowBlur: 20, shadowColor: "rgba(0,0,0,0.1)" } },
        data: seriesData,
      }],
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(chartRef.current);
    return () => { ro.disconnect(); window.removeEventListener("resize", onResize); try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; };
  }, [cardData]);

  return (
    <div className="avd-chart-cell">
      <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

// ── Column Chart ──────────────────────────────────────────────────────────
function ColumnChart({ cardData }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const keys = dashboardCardConfigForAvalanche.data;
    const maxVal = Math.max(...keys.map(c => cardData[c.key] || 0), 1);
    const totalAll = keys.reduce((s, c) => s + (cardData[c.key] || 0), 0) || 1;
    const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0"];
    const labelWidth = () => Math.max(34, Math.floor(((chartRef.current?.clientWidth || 300) - 16) / keys.length) - 4);

    const option = {
      title: {
        text: "CATEGORY COMPARISON", left: "center", top: 14,
        textStyle: { color: C.text1, fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" },
        subtext: "Count per impact type", subtextStyle: { color: C.text3, fontSize: 10 },
      },
      tooltip: {
        trigger: "axis", axisPointer: { type: "shadow" },
        confine: true,
        formatter: params => {
          const p = params[0];
          const pct = ((p.value / totalAll) * 100).toFixed(1);
          return `<div style="padding:6px 8px;min-width:160px"><div style="font-weight:800;color:${C.text1};margin-bottom:5px;font-size:12px">${p.name}</div><div style="color:${C.primary};font-weight:800;font-size:20px;line-height:1">${p.value.toLocaleString("en-IN")}</div><div style="color:${C.text3};font-size:11px;margin-top:3px">${pct}% of total</div></div>`;
        },
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);",
      },
      grid: { left: 8, right: 8, top: 72, bottom: 26, containLabel: true },
      xAxis: {
        type: "category", data: keys.map(c => formatLabel(c.key)),
        axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
        axisLabel: { color: C.text1, fontSize: 10, fontWeight: "600", interval: 0, width: labelWidth(), overflow: "break", lineHeight: 14, align: "center" },
      },
      yAxis: {
        type: "value", splitLine: { lineStyle: { color: C.border, type: "dashed" } },
        axisLabel: { color: C.text3, fontSize: 10 }, axisLine: { show: false }, axisTick: { show: false },
        max: maxVal * 1.15,
      },
      series: [{
        type: "bar", barWidth: "44%",
        data: keys.map((cfg, idx) => ({
          value: cardData[cfg.key] || 0,
          itemStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [{ offset: 0, color: blueShades[idx] }, { offset: 1, color: blueShades[idx] + "55" }],
            },
            borderRadius: [10, 10, 0, 0],
          },
        })),
        label: { show: true, position: "top", formatter: p => p.value.toLocaleString("en-IN"), fontSize: 12, fontWeight: "800", color: C.text1 },
        showBackground: true, backgroundStyle: { color: "rgba(42,101,145,.04)", borderRadius: [10, 10, 0, 0] },
        emphasis: { itemStyle: { shadowBlur: 16, shadowColor: "rgba(15,45,66,.12)" } },
      }],
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => {
      if (!chartInstance.current) return;
      chartInstance.current.setOption({ xAxis: { axisLabel: { width: labelWidth() } } });
      chartInstance.current.resize();
    };
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(chartRef.current);
    return () => { ro.disconnect(); window.removeEventListener("resize", onResize); try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; };
  }, [cardData]);

  return (
    <div className="avd-chart-cell">
      <div ref={chartRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

// ── Gauge Chart ───────────────────────────────────────────────────────────
function GaugeChart({ cardData }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const keys = dashboardCardConfigForAvalanche.data;
  const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0"];

  useEffect(() => {
    if (!chartRef.current) return;
    const maxVal = Math.max(...keys.map(c => cardData[c.key] || 0), 1);
    const ringDefs = [
      { inner: "72%", outer: "82%" },
      { inner: "56%", outer: "66%" },
      { inner: "40%", outer: "50%" },
      { inner: "24%", outer: "34%" },
    ];

    const rings = keys.map((cfg, i) => {
      const val = cardData[cfg.key] || 0;
      const pct = (val / (maxVal * 1.05)) * 100;
      const color = blueShades[i];
      const rd = ringDefs[i];
      return {
        type: "gauge", center: ["50%", "62%"], radius: rd.outer,
        startAngle: 180, endAngle: 0, min: 0, max: 100,
        pointer: { show: false },
        progress: {
          show: true, overlap: false, roundCap: true, clip: false,
          itemStyle: { color: { type: "linear", x: 0, y: 0, x2: 1, y2: 0, colorStops: [{ offset: 0, color: color + "99" }, { offset: 1, color: color }] } },
        },
        axisLine: { roundCap: true, lineStyle: { width: parseFloat(rd.outer) - parseFloat(rd.inner), color: [[1, "rgba(42,101,145,.08)"]] } },
        splitLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false }, anchor: { show: false },
        detail: { show: false }, title: { show: false },
        data: [{ value: pct, name: formatLabel(cfg.key) }],
      };
    });

    const option = {
      title: {
        text: "IMPACT GAUGES", left: "center", top: 14,
        textStyle: { color: C.text1, fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" },
        subtext: "Relative severity per category", subtextStyle: { color: C.text3, fontSize: 10 },
      },
      tooltip: {
        trigger: "item",
        formatter: p => {
          const cfg = keys.find(k => formatLabel(k.key) === p.name);
          if (!cfg) return "";
          const val = cardData[cfg.key] || 0;
          return `<div style="padding:6px 8px"><div style="font-weight:800;color:${C.text1};margin-bottom:4px;font-size:12px">${p.name}</div><div style="color:${C.primary};font-weight:800;font-size:20px">${val.toLocaleString("en-IN")}</div><div style="color:${C.text3};font-size:11px;margin-top:3px">${p.value.toFixed(1)}% of max</div></div>`;
        },
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);",
      },
      series: rings,
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(chartRef.current);
    return () => { ro.disconnect(); window.removeEventListener("resize", onResize); try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; };
  }, [cardData]);

  return (
    <div className="avd-chart-cell" style={{ display: "flex", flexDirection: "column" }}>
      <div ref={chartRef} style={{ flex: 1, width: "100%" }} />
      <div className="avd-gauge-legend">
        {keys.map((cfg, idx) => {
          const val = cardData[cfg.key] || 0;
          return (
            <div key={cfg.key} className="avd-gauge-legend-item">
              <div className="avd-gauge-legend-bar" style={{ background: `linear-gradient(90deg, ${blueShades[idx]}88, ${blueShades[idx]})` }} />
              <span className="avd-gauge-legend-val">{val.toLocaleString("en-IN")}</span>
              <span>{formatLabel(cfg.key)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}